import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../app/useAuth";
import { useEntitlements } from "../../hooks/useEntitlements";
import { createCheckoutSession } from "../../lib/data/billing";
import { TIER_ORDER } from "../../lib/tier-config";
import { cn } from "../../lib/utils";

/**
 * Display pricing. Final price points are confirmed against the blueprint ranges
 * (~$3-4 Lite / ~$6-8 Pro). Annual = 10x monthly (2 months free).
 */
const PRICING = {
  free: { month: 0, year: 0 },
  lite: { month: 3.99, year: 39.99 },
  pro: { month: 6.99, year: 69.99 },
};

const TIERS = ["free", "lite", "pro"];

const TIER_COPY = {
  free: {
    name: "Free",
    tagline: "Everything you need to schedule the next session.",
  },
  lite: {
    name: "Lite",
    tagline: "Automate the busywork for the table you run.",
  },
  pro: {
    name: "Pro",
    tagline: "Full calendar + Discord automation. Sponsor your whole table.",
  },
};

// Feature comparison rows. `tiers` maps each tier to true / false / a string.
// Pulled to read like the matrix; min-tier semantics mirror FEATURE_MIN_TIER so
// the page can't claim a feature a lower tier doesn't actually get.
const FEATURE_ROWS = [
  { label: "Vote (Feasible / Preferred) & RSVP", tiers: { free: true, lite: true, pro: true } },
  { label: "See results & guest voting", tiers: { free: true, lite: true, pro: true } },
  { label: "Discord voting", tiers: { free: true, lite: true, pro: true } },
  { label: "Questing groups", tiers: { free: "1", lite: "3", pro: "Unlimited" } },
  { label: "Concurrent polls", tiers: { free: "3", lite: "15", pro: "Unlimited" } },
  { label: "Participants per poll", tiers: { free: "15", lite: "40", pro: "Unlimited" } },
  { label: "History retention", tiers: { free: "90 days", lite: "Forever", pro: "Forever" } },
  { label: "Auto-create Google Calendar event", tiers: { free: false, lite: true, pro: true } },
  { label: "Rich calendar descriptions & attendees", tiers: { free: false, lite: true, pro: true } },
  { label: "Result sorting & required-attendance filter", tiers: { free: false, lite: true, pro: true } },
  { label: "Clone a session poll", tiers: { free: false, lite: true, pro: true } },
  { label: "Basic Discord poll card", tiers: { free: false, lite: true, pro: true } },
  { label: "Reschedule a finalized session", tiers: { free: false, lite: false, pro: true } },
  { label: "Two-way Google Calendar sync", tiers: { free: false, lite: false, pro: true } },
  { label: "Discord live card & role pings", tiers: { free: false, lite: false, pro: true } },
  { label: "Voter anonymization", tiers: { free: false, lite: false, pro: true } },
];

function formatPrice(amount) {
  if (amount === 0) return "$0";
  return `$${amount.toFixed(2).replace(/\.00$/, "")}`;
}

function CellValue({ value }) {
  if (value === true) return <Check className="mx-auto h-4 w-4 text-emerald-500" aria-label="Included" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-slate-300 dark:text-slate-600" aria-label="Not included" />;
  return <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{value}</span>;
}

export default function PricingPage() {
  const { user } = useAuth();
  const { effectiveTier } = useEntitlements();
  const [interval, setInterval] = useState("year"); // annual default
  const [busyTier, setBusyTier] = useState(null);

  const currentTier = user ? effectiveTier || "free" : null;

  const handleUpgrade = async (tier) => {
    if (!user) return;
    setBusyTier(tier);
    try {
      const { url } = await createCheckoutSession({
        tier,
        interval,
        successUrl: `${window.location.origin}/settings?checkout=success`,
        cancelUrl: `${window.location.origin}/pricing?checkout=cancel`,
      });
      // eslint-disable-next-line react-hooks/immutability -- intentional: navigate by assigning window.location.href
      if (url) window.location.href = url;
    } catch (err) {
      toast.error(err?.message || "Could not start checkout. Please try again.");
      setBusyTier(null);
    }
  };

  const intervalToggle = useMemo(
    () => (
      <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 text-sm dark:border-slate-700 dark:bg-slate-900">
        {[
          { id: "month", label: "Monthly" },
          { id: "year", label: "Annual" },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            aria-pressed={interval === opt.id}
            onClick={() => setInterval(opt.id)}
            className={cn(
              "rounded-full px-4 py-1.5 font-semibold transition-colors",
              interval === opt.id
                ? "bg-brand-primary text-white"
                : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
            )}
          >
            {opt.label}
          </button>
        ))}
        <span className="ml-1 mr-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
          2 months free
        </span>
      </div>
    ),
    [interval]
  );

  return (
    <div className="dark min-h-screen" style={{ colorScheme: "dark" }}>
      <div className="relative min-h-screen text-slate-100">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[url('/assets/background.jpeg')] bg-cover bg-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/70 to-slate-950/85" />
        </div>

        <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="/app_icon.png" alt="Quest Scheduler" className="h-10 w-10 rounded-xl object-contain" />
            <span className="font-display text-xl tracking-[0.18em] text-white">Quest Scheduler</span>
          </Link>
          <Link
            to={user ? "/dashboard" : "/auth"}
            className="rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white hover:bg-brand-primary/90"
          >
            {user ? "Dashboard" : "Sign in"}
          </Link>
        </header>

        <main className="mx-auto max-w-6xl px-6 pb-20">
          <section className="mx-auto max-w-3xl text-center">
            <h1 className="font-display text-4xl tracking-wide text-white sm:text-5xl">
              Pricing for tables of every size
            </h1>
            <p className="mt-4 text-base text-slate-300">
              Voting, RSVPs, results and guest access are <strong>always free</strong>. Upgrade only the
              organizer who runs the table — <strong>one organizer pays, the whole party benefits.</strong>
            </p>
            <div className="mt-8 flex justify-center">{intervalToggle}</div>
          </section>

          {/* Tier cards */}
          <section className="mt-12 grid gap-6 md:grid-cols-3">
            {TIERS.map((tier) => {
              const copy = TIER_COPY[tier];
              const price = PRICING[tier][interval];
              const isCurrent = currentTier === tier;
              const canUpgrade =
                user && tier !== "free" && TIER_ORDER[tier] > TIER_ORDER[currentTier || "free"];
              const highlight = tier === "pro";
              return (
                <div
                  key={tier}
                  data-tier-card={tier}
                  className={cn(
                    "flex flex-col rounded-3xl border bg-slate-900/80 p-6 backdrop-blur",
                    highlight
                      ? "border-amber-400/60 shadow-xl shadow-amber-500/10 ring-1 ring-amber-400/30"
                      : "border-slate-700/70"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white">{copy.name}</h2>
                    {highlight && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[11px] font-semibold text-amber-200">
                        <Sparkles className="h-3 w-3" /> Most automation
                      </span>
                    )}
                  </div>
                  <p className="mt-2 min-h-[40px] text-sm text-slate-300">{copy.tagline}</p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">{formatPrice(price)}</span>
                    {tier !== "free" && (
                      <span className="text-sm text-slate-400">/{interval === "year" ? "yr" : "mo"}</span>
                    )}
                  </div>

                  <div className="mt-6">
                    {tier === "free" ? (
                      <Link
                        to={user ? "/dashboard" : "/auth"}
                        data-testid="upgrade-cta-free"
                        className="block w-full rounded-full border border-slate-600 px-4 py-2 text-center text-sm font-semibold text-slate-200 hover:bg-slate-800"
                      >
                        {user ? "Go to dashboard" : "Get started free"}
                      </Link>
                    ) : !user ? (
                      <Link
                        to="/auth"
                        data-testid={`upgrade-cta-${tier}`}
                        className="block w-full rounded-full bg-brand-primary px-4 py-2 text-center text-sm font-semibold text-white hover:bg-brand-primary/90"
                      >
                        Sign in to upgrade
                      </Link>
                    ) : (
                      <button
                        type="button"
                        data-testid={`upgrade-cta-${tier}`}
                        disabled={isCurrent || !canUpgrade || busyTier === tier}
                        onClick={() => handleUpgrade(tier)}
                        className={cn(
                          "w-full rounded-full px-4 py-2 text-center text-sm font-semibold transition-colors",
                          isCurrent || !canUpgrade
                            ? "cursor-default bg-slate-700 text-slate-300"
                            : "bg-brand-primary text-white hover:bg-brand-primary/90",
                          busyTier === tier && "opacity-70"
                        )}
                      >
                        {isCurrent
                          ? "Current plan"
                          : !canUpgrade
                            ? "Included in your plan"
                            : busyTier === tier
                              ? "Starting checkout…"
                              : `Upgrade to ${copy.name}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </section>

          {/* Comparison table */}
          <section className="mt-14 overflow-hidden rounded-3xl border border-slate-700/70 bg-slate-900/70 backdrop-blur">
            {/* Scroll the 4-column matrix horizontally on a phone instead of
                crushing the columns to nothing inside the rounded (overflow-hidden) card. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-700/70 text-slate-300">
                  <th className="px-4 py-3 font-semibold">Feature</th>
                  {TIERS.map((tier) => (
                    <th key={tier} className="px-4 py-3 text-center font-semibold capitalize">
                      {TIER_COPY[tier].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((row) => (
                  <tr key={row.label} className="border-b border-slate-800/60 last:border-0">
                    <td className="px-4 py-3 text-slate-200">{row.label}</td>
                    {TIERS.map((tier) => (
                      <td key={tier} className="px-4 py-3 text-center">
                        <CellValue value={row.tiers[tier]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mx-auto mt-14 max-w-3xl space-y-4">
            <h2 className="text-center font-display text-2xl text-white">Questions</h2>
            {[
              {
                q: "Do invited players have to pay?",
                a: "Never. Voting, RSVPs, seeing results and Discord voting are free on every tier, forever. Only the organizer who wants automation upgrades.",
              },
              {
                q: "What happens if I downgrade or my subscription lapses?",
                a: "Nothing is deleted. Premium automation (like two-way calendar sync) quietly pauses and your data, votes and basic polls keep working. It snaps back the moment you renew.",
              },
              {
                q: "Can I switch between monthly and annual?",
                a: "Yes — manage your plan any time from the billing portal in Settings. Annual gives you two months free.",
              },
            ].map((item) => (
              <div
                key={item.q}
                className="rounded-2xl border border-slate-700/70 bg-slate-900/70 p-4 backdrop-blur"
              >
                <p className="font-semibold text-white">{item.q}</p>
                <p className="mt-1 text-sm text-slate-300">{item.a}</p>
              </div>
            ))}
          </section>
        </main>
      </div>
    </div>
  );
}
